import { formatDate } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { Observable, take } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Page } from '../../../shared/models/page.model';
import { Budget } from '../../models/budget';
import { NewOrderItem } from '../../models/newOrderItem';
import { OrderForm } from '../../models/order';
import { OrderItem } from '../../models/orderItem';
import { OrderResponse } from '../../models/orderResponse';


@Injectable({
  providedIn: 'root'
})
export class OrdersService {

  constructor(private http: HttpClient) {
  }

  ordersUrl: string = `${environment.apiUrl}/api/orders`;
  budgetsUrl: string = `${environment.apiUrl}/api/budgets`;
  itemsUrl: string = `${environment.apiUrl}/api/items`;
  newOrderUrl: string = `${environment.apiUrl}/api/new-order`;
  suppliersUrl: string = `${environment.apiUrl}/api/suppliers`;

  createOrder(orderForm: OrderForm) {
    let url = `${this.ordersUrl}/create`;
    return this.http.post(url, orderForm);
  }

  approveOrder(orderId: number) {
    let url = `${this.ordersUrl}/approve/${orderId}`;
    return this.http.put(url, {});
  }

  rejectOrder(orderId: number, justification: string) {
    const url = `${this.ordersUrl}/reject/${orderId}`;
    return this.http.put(url, justification);
  }

  getOrders(pager: PageEvent, orderId?: string, projectType?: string, projectDescription?: string, requestDate?: Date, requesterId?: number, status?: string): Observable<Page<any>> {
    let params = new HttpParams()
      .append('page', pager.pageIndex)
      .append('size', pager.pageSize)

    if (orderId) params = params.set('orderId', orderId);

    if (projectType) params = params.set('projectType', projectType);

    if (projectDescription) params = params.set('projectDescription', projectDescription);

    if (requestDate) {
      const formattedDate = formatDate(requestDate, 'yyyy-MM-dd', 'en-US');
      params = params.append('requestDate', formattedDate);
    }

    if (requesterId) params = params.set('requesterId', requesterId);

    if (status) params = params.set('status', status);

    return this.http.get<Page<any>>(this.ordersUrl, {params});
  }

  listNewOrderItems(page: number, size: number, filter: string, categoryId?: number): Observable<Page<NewOrderItem>> {
    let url = `${this.newOrderUrl}/list?page=${page}&size=${size}&filter=${encodeURIComponent(filter)}`;
    if (categoryId) {
      url += `&categoryId=${categoryId}`;
    }
    return this.http.get<Page<NewOrderItem>>(url);
  }

  getImage(id: number, isKit: boolean) {
    const fullImageUrl = `${this.newOrderUrl}/images?id=${id}&isKit=${isKit}`;
    return this.http.get(fullImageUrl, { responseType: 'blob' }).pipe(take(1));
  }

  listItemsGroupedByCategory() {
    let url = `${this.itemsUrl}/grouped-by-category`;
    return this.http.get(url);
  }

  getOrderDetails(orderId: number): Observable<OrderItem[]> {
    const url = `${this.ordersUrl}/${orderId}/details`;
    return this.http.get<OrderItem[]>(url);
  }

  addSupplierToOrder(orderId: number, supplierId: number, expectedReturnDate: Date): Observable<any> {
    const url = `${this.budgetsUrl}/orders/${orderId}/budgets`;
    const body = {
      supplierId,
      expectedReturnDate
    };
    return this.http.post(url, body);
  }

  getAllBudgetsByOrder(orderId: number): Observable<Budget[]> {
    const url = `${this.budgetsUrl}/orders/${orderId}/all-budgets`;
    return this.http.get<Budget[]>(url);
  }

  getReleasedBudgetsByOrder(orderId: number): Observable<Budget[]> {
    const url = `${this.budgetsUrl}/orders/${orderId}/released-budgets`;
    return this.http.get<Budget[]>(url);
  }

  getEmailsBySupplierIds(supplierIds: number[]): Observable<string[]> {
    const url = `${this.suppliersUrl}/emails-by-ids`;
    const body = { supplierIds };
    return this.http.post<string[]>(url, body);
  }

  getOrderItems(orderId: number) {
    const url = `${this.ordersUrl}/${orderId}/items`;
    return this.http.get<OrderResponse>(url);
  }

  releaseBudgetsToApproval(orderId: string | null) {
    const url = `${this.ordersUrl}/release-budgets-to-approve/${orderId}`;
    return this.http.put(url, {});
  }

  updateOrder(orderId: string, data: OrderForm) {
    const url = `${this.ordersUrl}/update/${orderId}`;
    return this.http.put(url, data);
  }

  deactivateOrder(orderId: number): Observable<any> {
    const url = `${this.ordersUrl}/deactivate/${orderId}`;
    return this.http.put(url, orderId);
  }

  sendOrder(orderId: number): Observable<any> {
    const url = `${this.ordersUrl}/${orderId}/submit-for-approval`;
    return this.http.post(url, orderId);
  }

  cancelOrder(orderId: number, justification: string): Observable<any> {
    const url = `${this.ordersUrl}/cancel/${orderId}`;
    const body =  justification ;
    return this.http.put(url, body);
  }


}
